// src/reducers/authSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { apiURL } from '.././config/default'; // Adjust the path as needed
import setAuthHeader from '.././utils/setAuthHeader'; // Adjust the path as needed
import { alert } from '../actions/alert'; // Import the alert action

// Initial state
const initialState = {
  token: localStorage.getItem('token') || null,
  isAuth: false,
  loading: true,
  user: null,
  permissions: [],
};

// Async Thunks
export const register = createAsyncThunk(
  'auth/register',
  async ({ adminSecretKey, email, fullName, userName, password, role }, { dispatch }) => {
    const _config = {
      headers: {
        'Content-Type': 'application/json',
      },
    };

    const body = JSON.stringify({ adminSecretKey, email, fullName, userName, password, role });

    try {
      const res = await axios.post(`${apiURL}admin/register`, body, _config);
      return res.data;
    } catch (err) {
      const errors = err.response?.data?.error || [];
      if (errors.length > 0) {
        errors.forEach((error) => {
          dispatch(alert(error.msg, 'danger', { marginTop: '3em' }));
        });
      }
      throw err; // Rethrow the error to trigger rejected state
    }
  }
);

export const login = createAsyncThunk(
  'auth/login',
  async ({ email, password }, { dispatch }) => {
    const _config = {
      headers: {
        'Content-Type': 'application/json',
      },
    };

    const body = JSON.stringify({ email, password });

    try {
      const res = await axios.post(`${apiURL}admin/login`, body, _config);
      // console.log("object",res);
      return res.data;
    } catch (err) {
      throw err; // Rethrow the error to trigger rejected state
    }
  }
);

export const auth = createAsyncThunk(
  'auth/auth',
  async (_, { dispatch }) => {
    const token = localStorage.getItem('token');
   
    // ⛔ STOP: No token → do NOT call API
    if (!token) {
      console.log("No token found → Skipping API calls");
      throw new Error("No token found");
      // return null; // thunk resolves with null
    }

    // Token exists → set auth header
    setAuthHeader(token);

    try {
      const res = await axios.get(`${apiURL}auth`);
     console.log("ress",res)
      // Check if Brand data exists and is not older than 24 hours
      const brandData = localStorage.getItem('Brand');
      const brandTimestamp = localStorage.getItem('BrandTimestamp');
      const now = Date.now();

      let brandList;

      if (brandData && brandTimestamp && now - parseInt(brandTimestamp, 10) < 24 * 60 * 60 * 1000) {
        // console.log("brandData",brandData)
        // Use cached brand data
        brandList = JSON?.parse(brandData);
      } else {
        // Fetch fresh data and cache it with a timestamp
        // const brand = await axios.get(`${apiURL}admin/all-stores`);
        brandList = res?.data?.stores?.map((item) => ({
          id: item?._id,
          value: item?.fullName,
          label: item?.fullName,
        }));
        // console.log("brandList",brandList)

        localStorage.setItem('Brand', JSON.stringify(brandList));
        localStorage.setItem('BrandTimestamp', now.toString());
      }

      // Optionally dispatch or use brandList here if needed
      return res?.data;
    } catch (err) {
      console.error(err);
      throw err; // Trigger rejected state
    }
  }
);


export const logout = createAsyncThunk('auth/logout', (_, { dispatch }) => {
  delete axios.defaults.headers.common['x-auth-token'];
  localStorage.removeItem('token');
  localStorage.removeItem('User');
  localStorage.removeItem('Brand');
  dispatch({ type: 'auth/logout/reset' }); // optional: reset slice immediately
});


export const forgot = createAsyncThunk(
  'auth/forgot',
  async ({ email }, { dispatch }) => {
    const _config = {
      headers: {
        'Content-Type': 'application/json',
      },
    };

    const body = JSON.stringify({ email });

    try {
      const res = await axios.post(`${apiURL}forgot`, body, _config);
      return res.data;
    } catch (err) {
      const errors = err.response?.data?.error || [];
      if (errors.length > 0) {
        errors.forEach((error) => {
          dispatch(alert(error.msg, 'danger', { marginTop: '3em' }));
        });
      }
      throw err; // Rethrow the error to trigger rejected state
    }
  }
);

export const reset = createAsyncThunk(
  'auth/reset',
  async ({ id, password }, { dispatch }) => {
    const _config = {
      headers: {
        'Content-Type': 'application/json',
      },
    };

    setAuthHeader(id);

    const body = JSON.stringify({ id, password });

    try {
      const res = await axios.post(`${apiURL}reset`, body, _config);
      delete axios.defaults.headers.common['x-auth-token'];
      return res.data;
    } catch (err) {
      const errors = err.response?.data?.error || [];
      if (errors.length > 0) {
        errors.forEach((error) => {
          dispatch(alert(error.msg, 'danger', { marginTop: '3em' }));
        });
      }
      throw err; // Rethrow the error to trigger rejected state
    }
  }
);

// Auth Slice
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Register
      .addCase(register.fulfilled, (state) => {
        state.isAuth = null;
        state.loading = false;
      })
      .addCase(register.rejected, (state) => {
        state.token = null;
        state.isAuth = false;
        state.loading = false;
        state.user = null;
      })

      // Login
      .addCase(login.fulfilled, (state, action) => {
        localStorage.setItem('token', action.payload.token);
        state.token = action.payload.token;
        state.isAuth = true;
        state.loading = false;
      })
      .addCase(login.rejected, (state) => {
        state.token = null;
        state.isAuth = false;
        state.loading = false;
        state.user = null;
      })

      // Auth
      .addCase(auth.fulfilled, (state, action) => {
        const { user, permissions } = action.payload;

        state.user = user;
        state.permissions = permissions; // ✅ store permissions
        state.isAuth = true;
        state.loading = false;
      })
      .addCase(auth.rejected, (state) => {
        state.token = null;
        state.isAuth = false;
        state.loading = false;
        state.user = null;
      })

      // Logout
      .addCase(logout.fulfilled, (state) => {
        localStorage.removeItem('token');
        state.token = null;
        state.isAuth = false;
        state.loading = false;
        state.user = null;
      })

      // Forgot Password
      .addCase(forgot.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(forgot.rejected, (state) => {
        state.loading = false;
      })

      // Reset Password
      .addCase(reset.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(reset.rejected, (state) => {
        state.loading = false;
      });
  },
});

export default authSlice.reducer;